"use client";

import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./icons";
import { usePopup } from "./usePopup";

interface SelectOption<V> {
  value: V;
  label: string;
}

/** The list wants at least this much room below its button before it opens above it (px). */
const MIN_BELOW = 160;
/** The list's distance to its button and to the window's edges (px). */
const GAP = 4;
const EDGE = 8;

/**
 * Where the list opens: fixed at its button — below it, above it when the window leaves too little room below and
 * more above — at least as wide as the button (at most the window's width), scrolling in itself. Fixed, it lies over
 * every box that scrolls or cuts its content (a table, a window's body). It starts at the button's left edge; `inView`
 * moves it left once its width is known.
 */
function listPlace(button: HTMLElement): CSSProperties {
  const r = button.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - EDGE;
  const above = r.top - EDGE;
  const vertical = below < MIN_BELOW && above > below ? { bottom: window.innerHeight - r.top + GAP, maxHeight: above - GAP } : { top: r.bottom + GAP, maxHeight: below - GAP };
  const widest = window.innerWidth - 2 * EDGE;
  return { position: "fixed", left: r.left, minWidth: Math.min(r.width, widest), maxWidth: widest, ...vertical };
}

/** The list's left edge so that all of it is in the window: moved left as far as it reaches past the right edge. */
const inView = (left: number, width: number) => Math.max(EDGE, Math.min(left, window.innerWidth - EDGE - width));

/** A list that opens at its button (`listPlace`), kept in the window, closed by a press beside it, Escape, scrolling or resizing. */
function useListPopup() {
  const { open, setOpen, ref } = usePopup<HTMLDivElement>();
  const [place, setPlace] = useState<CSSProperties>({});
  const list = useRef<HTMLDivElement>(null);
  // the list measured: kept inside the window (before it is painted)
  useLayoutEffect(() => {
    const el = list.current;
    if (!open || !el || typeof place.left !== "number") return;
    const left = inView(place.left, el.offsetWidth);
    if (left !== place.left) setPlace({ ...place, left });
  }, [open, place]);
  // the list is fixed where it opened: moving the page away from it closes it (not a scroll inside the list)
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => !ref.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, ref, setOpen]);
  const toggle = () => {
    if (ref.current) setPlace(listPlace(ref.current));
    setOpen((o) => !o);
  };
  return { open, setOpen, ref, place, list, toggle };
}

type ListPopup = ReturnType<typeof useListPopup>;

/** The slim button and, while open, the list of options under it — one look for the single and the multiple choice. */
function ListBox({ popup, label, shown, prefix, disabled, multi, className, children }: { popup: ListPopup; label: string; shown: string; prefix?: ReactNode; disabled: boolean; multi: boolean; className?: string; children: ReactNode }) {
  const { open, ref, place, list, toggle } = popup;
  return (
    <div ref={ref} className={clsx("relative flex min-w-0", className)}>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${shown}`}
        title={multi ? shown : undefined}
        className={clsx("inline-flex h-6 w-full min-w-0 cursor-pointer items-center gap-1 rounded-md px-1.5 text-[11.5px] transition-colors disabled:cursor-default disabled:opacity-60", open ? "bg-accent-soft" : "bg-surface-2 hover:text-ink")}
      >
        {prefix && <span className="hidden shrink-0 text-muted wide:md:inline">{prefix}</span>}
        <span className="min-w-0 flex-1 truncate text-left font-medium text-ink">{shown}</span>
        <Icon name="chevronDown" className={clsx("h-3.5 w-3.5 shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div ref={list} role="listbox" aria-label={label} aria-multiselectable={multi || undefined} style={place} className="scrollbar-thin z-[110] w-max overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-lift">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * A choice as the app shows it everywhere (the chart's timeframe, the settings): a slim button with the chosen label and a
 * chevron, and below it the options, the chosen one ticked. A press beside it, Escape, scrolling or resizing the
 * window closes it. `prefix`: a quiet word before the label (from 768 px).
 */
export function Select<V extends string | number | null>({ label, value, options, onChange, prefix, disabled = false, className }: { label: string; value: V; options: readonly SelectOption<V>[]; onChange: (value: V) => void; prefix?: ReactNode; disabled?: boolean; className?: string }) {
  const popup = useListPopup();
  const current = options.find((o) => o.value === value);
  const choose = (v: V) => {
    onChange(v);
    popup.setOpen(false);
  };
  return (
    <ListBox popup={popup} label={label} shown={current?.label ?? String(value ?? "")} prefix={prefix} disabled={disabled} multi={false} className={className}>
      {options.map((o) => <Option key={String(o.value)} option={o} chosen={o.value === value} onChoose={choose} />)}
    </ListBox>
  );
}

/**
 * Several choices in the same slim button: the chosen labels in it (`none` when none is), the options ticked in the
 * list, which stays open while they are toggled.
 */
export function MultiSelect<V extends string>({ label, values, options, onChange, none, className }: { label: string; values: readonly V[]; options: readonly SelectOption<V>[]; onChange: (values: V[]) => void; none: string; className?: string }) {
  const popup = useListPopup();
  const chosen = options.filter((o) => values.includes(o.value));
  const toggle = (v: V) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <ListBox popup={popup} label={label} shown={chosen.length > 0 ? chosen.map((o) => o.label).join(", ") : none} disabled={false} multi className={className}>
      {options.map((o) => <Option key={o.value} option={o} chosen={values.includes(o.value)} onChoose={toggle} />)}
    </ListBox>
  );
}

/** One option of the list: its label, ticked when chosen. */
function Option<V>({ option, chosen, onChoose }: { option: SelectOption<V>; chosen: boolean; onChoose: (value: V) => void }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={chosen}
      onClick={() => onChoose(option.value)}
      className={clsx("flex min-h-6 w-full cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[11.5px] leading-4 transition-colors", chosen ? "text-accent-ink" : "text-ink-2 hover:bg-surface-2 hover:text-ink")}
    >
      <Icon name="check" className={clsx("h-3 w-3 shrink-0", !chosen && "invisible")} />
      {option.label}
    </button>
  );
}
